import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from '../../components/ui';
import { apiErrorMessage } from '../../components/admin/locations/locationForm';
import { editorialService, EditorialAction } from '../../services/editorial';
import { EditorialList, EditorialPost, EditorialStatus } from '../../types/editorial';
import {
  agendaGroups, EditorialForm, editorialDirty, emptyEditorialForm, formFromPost,
  formatBrasilia, inputFromEditorialForm, isOverdue, statusLabels,
} from '../../components/admin/editorial/editorialForm';
import {
  AgendaItem, Field, Grid, Head, Hint, Note, Page, Panel, Preview, Row, Stack, Status,
} from '../../components/admin/editorial/editorialStyles';
import EditorialFields from '../../components/admin/editorial/EditorialFields';
import EditorialReview from '../../components/admin/editorial/EditorialReview';
import { useUnsavedEditorialChanges } from '../../components/admin/editorial/useUnsavedEditorialChanges';

const actionMessages: Record<EditorialAction, string> = {
  submit: 'Artigo enviado para aprovação.', approve: 'Versão aprovada e publicação agendada.',
  'request-changes': 'Ajustes solicitados. O artigo aguarda correção e nova aprovação.', archive: 'Programação arquivada.',
};

const AdminEditorial: React.FC = () => {
  const [posts, setPosts] = useState<EditorialPost[]>([]);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EditorialForm>(emptyEditorialForm());
  const [filter, setFilter] = useState<EditorialStatus | 'active' | ''>('active');
  const [preview, setPreview] = useState(false);
  const [reviewerNote, setReviewerNote] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const editorRef = useRef<HTMLFormElement>(null);
  const selected = posts.find(post => post.id === selectedId) || null;
  const dirty = editorialDirty(form, selected);
  useUnsavedEditorialChanges(dirty);
  const groups = useMemo(() => agendaGroups(posts.filter(post => filter === 'active'
    ? !['published', 'archived'].includes(post.status) : !filter || post.status === filter)), [posts, filter]);
  const pendingCount = posts.filter(post => post.status === 'pending_review').length;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await editorialService.list();
      setPosts(result.items);
      setCanApprove(result.can_approve);
    } catch (err) { setError(apiErrorMessage(err, 'Não foi possível carregar a programação. Tente novamente.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    editorRef.current?.scrollIntoView?.({ block: 'start' });
  }, [selectedId, creating]);

  const select = (post: EditorialPost | null) => {
    if (dirty && !window.confirm('Descartar as alterações não salvas deste artigo?')) return;
    setSelectedId(post?.id || null);
    setCreating(!post);
    setForm(post ? formFromPost(post) : emptyEditorialForm());
    setPreview(false);
    setReviewerNote('');
    setError('');
    setMessage('');
  };
  const replace = (post: EditorialPost) => {
    setPosts(current => current.some(item => item.id === post.id)
      ? current.map(item => item.id === post.id ? post : item) : [...current, post]);
    setSelectedId(post.id);
    setCreating(false);
    setForm(formFromPost(post));
    setReviewerNote('');
  };
  const updateField = (field: keyof EditorialForm, value: string) => {
    setForm(current => {
      const next = { ...current, [field]: value };
      const currentSlug = current.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (field === 'title' && !selected?.published_at && (!current.slug || current.slug === currentSlug)) {
        next.slug = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      return next;
    });
    setMessage('');
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(''); setMessage('');
    try {
      const input = inputFromEditorialForm(form);
      const saved = selected ? await editorialService.update(selected.id, selected.revision, input) : await editorialService.create(input);
      replace(saved);
      setMessage('Rascunho salvo. Confira a prévia e envie para aprovação.');
    } catch (err) { setError(apiErrorMessage(err, err instanceof Error ? err.message : 'Não foi possível salvar o artigo.')); }
    finally { setBusy(false); }
  };
  const act = async (action: EditorialAction) => {
    if (!selected || dirty || busy || (action === 'request-changes' && !reviewerNote.trim())) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const saved = await editorialService.act(selected.id, selected.revision, action, reviewerNote.trim());
      replace(saved);
      setMessage(actionMessages[action]);
    } catch (err) { setError(apiErrorMessage(err, 'Não foi possível concluir a ação. Recarregue a versão salva e tente novamente.')); }
    finally { setBusy(false); }
  };
  const refreshSelected = async () => {
    if (!selected || (dirty && !window.confirm('Descartar suas alterações e carregar a última versão salva?'))) return;
    setBusy(true); setError('');
    try { replace(await editorialService.get(selected.id)); setMessage('Última versão carregada.'); }
    catch (err) { setError(apiErrorMessage(err, 'Não foi possível atualizar o artigo.')); }
    finally { setBusy(false); }
  };
  const refreshAgenda = async () => {
    if (dirty && !window.confirm('Descartar suas alterações e atualizar a programação?')) return;
    setBusy(true); setError('');
    try {
      const result = await editorialService.list();
      receiveAgenda(result);
      setMessage('Programação atualizada.');
    } catch (err) { setError(apiErrorMessage(err, 'Não foi possível atualizar a programação.')); }
    finally { setBusy(false); }
  };
  const receiveAgenda = (result: EditorialList) => {
    setPosts(result.items);
    setCanApprove(result.can_approve);
    const current = result.items.find(post => post.id === selectedId);
    setForm(current ? formFromPost(current) : emptyEditorialForm());
    if (!current) setSelectedId(null);
  };
  const importExisting = async () => {
    if (dirty || busy) return;
    setBusy(true); setError('');
    try {
      const result = await editorialService.importExisting();
      receiveAgenda(await editorialService.list());
      setMessage(result.imported > 0 ? `${result.imported} artigos já publicados incluídos na revisão.` : 'Os artigos já publicados estão todos disponíveis nesta programação.');
    } catch (err) { setError(apiErrorMessage(err, 'Não foi possível trazer os artigos já publicados.')); }
    finally { setBusy(false); }
  };

  return <Page>
    <Head>
      <div><h1>Blog e aprovação</h1><p>Planeje os próximos artigos, revise o conteúdo e acompanhe o que vai entrar no site.</p></div>
      <Button type="button" onClick={() => select(null)} disabled={busy || loading}>Novo artigo</Button>
    </Head>
    {error && <Note $warning role="alert" style={{ marginBottom: '1rem' }}>{error} {!selected && <button type="button" onClick={load}>Tentar novamente</button>}</Note>}
    {message && <Note role="status" style={{ marginBottom: '1rem' }}>{message}</Note>}
    <Grid>
      <Panel aria-labelledby="agenda-heading">
        <Row><h2 id="agenda-heading">Programação do blog</h2><Status>{pendingCount} em aprovação</Status></Row>
        <Hint>Datas e horários de Brasília.</Hint>
        <Row><Button type="button" $variant="outline" $size="sm" disabled={busy || loading} onClick={refreshAgenda}>Atualizar agenda</Button></Row>
        {canApprove && <Row><Button type="button" $variant="outline" $size="sm" disabled={busy || loading || dirty} onClick={importExisting}>Trazer artigos já publicados</Button></Row>}
        <Field>Filtrar por situação
          <select value={filter} onChange={event => setFilter(event.target.value as EditorialStatus | 'active' | '')}>
            <option value="active">Em andamento</option>
            <option value="">Todas as situações</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        {loading ? <div role="status"><Spinner /><Hint>Carregando programação…</Hint></div> : groups.length === 0
          ? <Hint>{filter ? 'Nenhum artigo nesta situação. Escolha outro filtro.' : 'Nenhum artigo planejado. Use “Novo artigo” para começar.'}</Hint>
          : groups.map(group => <Stack key={group.label}>
            <h3>{group.label}</h3>
            {group.posts.map(post => <AgendaItem key={post.id} type="button" $active={post.id === selectedId} aria-pressed={post.id === selectedId}
              disabled={busy} onClick={() => select(post)}>
              <small>{formatBrasilia(post.scheduled_at)}</small>
              <strong>{post.title}</strong>
              <Status $warning={post.status === 'changes_requested'}>{statusLabels[post.status]}</Status>
              {isOverdue(post) && <Status $warning>Atrasado · aprovação pendente</Status>}
            </AgendaItem>)}
          </Stack>)}
      </Panel>
      {!selected && !creating ? <Panel>
        <h2>Conteúdo revisado antes de entrar no site</h2>
        <p>Selecione um artigo da programação para ler, editar e acompanhar a aprovação.</p>
        <Note>Sem aprovação, não será publicado.</Note>
        <Hint>Fluxo: rascunho → aprovação de um administrador → publicação na data agendada.</Hint>
      </Panel> : <form ref={editorRef} onSubmit={save}>
        <Stack>
          <Panel>
            <Row><h2>{creating ? 'Novo artigo' : `Versão ${selected?.revision}`}</h2>{dirty && <Status $warning>Alterações não salvas</Status>}</Row>
            <Row>
              <Button type="button" $variant={preview ? 'secondary' : 'primary'} onClick={() => setPreview(false)}>Editar artigo</Button>
              <Button type="button" $variant={preview ? 'primary' : 'secondary'} disabled={!selected} onClick={() => setPreview(true)}>Prévia da versão salva</Button>
              <Button type="submit" disabled={busy || (!dirty && !!selected)}>{busy ? 'Aguarde…' : 'Salvar rascunho'}</Button>
              {selected && <Button type="button" $variant="secondary" disabled={busy} onClick={refreshSelected}>Recarregar versão</Button>}
            </Row>
            {!selected && <Hint>Salve o rascunho para conferir a prévia e enviar para aprovação.</Hint>}
          </Panel>
          {preview && selected ? <Panel>
            <Note>{dirty ? 'Esta prévia mostra a versão salva. Salve suas alterações para atualizar a prévia.' : `Prévia da versão ${selected.revision} salva no sistema.`}</Note>
            <Preview>
              {selected.featured_image && <img src={selected.featured_image} alt={selected.featured_image_alt || ''} />}
              <h1>{selected.title}</h1><Hint>Por {selected.author_name || 'Autor ainda não definido'}</Hint>
              <p>{selected.excerpt}</p>
              <div dangerouslySetInnerHTML={{ __html: selected.content }} />
              {selected.author_bio && <Hint>{selected.author_bio}</Hint>}
            </Preview>
          </Panel> : <EditorialFields form={form} onChange={updateField} published={!!selected?.published_at} disabled={busy} />}
          {selected && <EditorialReview post={selected} dirty={dirty} busy={busy} canApprove={canApprove} note={reviewerNote} onNote={setReviewerNote} onAction={act} />}
        </Stack>
      </form>}
    </Grid>
  </Page>;
};

export default AdminEditorial;
